import { CANONICAL_FIELDS, type EntityType } from "@cmh/core";
import { useState } from "react";
import { errorMessage, patch, post, type FieldMapping, type MappingSpec, type Migration } from "../../lib/api";
import { Alert, Badge, Button, Card, Input, Modal, Select, Table, Td, Textarea } from "../../components/ui";

interface PreviewResult {
  columns: string[];
  results: Array<{ source: Record<string, unknown>; record: { key: string; fields: Record<string, unknown>; consent?: Record<string, { status: string; at?: string }>; lists?: string[] } | null; skipped: string | null; errors: string[] }>;
}

const TRANSFORM_OPS = ["trim", "lower", "upper", "title", "split", "concat", "valueMap", "date", "phoneE164", "number", "boolean", "default", "regexReplace", "list"];

function targetOptions(entity: string): string[] {
  const base = [...(CANONICAL_FIELDS[entity as EntityType] ?? [])];
  if (entity === "contact") base.push("consent.email.status", "consent.email.at", "consent.email.method", "consent.email.reason", "consent.sms.status", "consent.sms.at", "consent.sms.method", "lists");
  if (entity === "order") base.push("line_item.sku", "line_item.title", "line_item.quantity", "line_item.price");
  return base;
}

function describeTransform(t: Record<string, unknown>): string {
  const { op, ...args } = t;
  const a = Object.entries(args)
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(" ");
  return a ? `${op}(${a})` : String(op);
}

/** Field mapping editor with live preview on 20 sample rows (ENG-2). */
export function MappingEditor({ ws, migration, editable, onSaved }: { ws: string; migration: Migration; editable: boolean; onSaved: () => void }) {
  const [spec, setSpec] = useState<MappingSpec>(migration.mapping);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [json, setJson] = useState("");
  const [msg, setMsg] = useState<{ tone: "ok" | "danger" | "warn"; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const targets = targetOptions(migration.entity);

  const update = (i: number, fm: FieldMapping) => {
    const fields = [...spec.fields];
    fields[i] = fm;
    setSpec({ ...spec, fields });
    setDirty(true);
  };

  const runPreview = async () => {
    setBusy("preview");
    setMsg(null);
    try {
      setPreview(await post<PreviewResult>(`/api/workspaces/${ws}/migrations/${migration.id}/preview-mapping`, { mapping: spec }));
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    setMsg(null);
    try {
      await patch(`/api/workspaces/${ws}/migrations/${migration.id}`, { mapping: spec });
      setDirty(false);
      setMsg({ tone: "ok", text: "Mapping saved. Run a new dry run to see its effect." });
      onSaved();
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  const columns = preview?.columns ?? [];
  const sourceList = (s: FieldMapping["source"]) => (s === undefined ? [] : Array.isArray(s) ? s : [s]);

  return (
    <Card
      title="Field mapping"
      actions={
        <>
          <Button size="sm" variant="secondary" onClick={runPreview} loading={busy === "preview"}>
            Preview 20 rows
          </Button>
          {editable && (
            <Button size="sm" onClick={save} disabled={!dirty} loading={busy === "save"}>
              Save mapping
            </Button>
          )}
        </>
      }
    >
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <p className="muted mb-3 text-sm">
        Match key: <b>{spec.keyField}</b>. Records are upserted by this field, so re-running never creates duplicates. Empty source values never overwrite existing data.
      </p>
      <Table head={["Target field", "Source column(s)", "Transforms", ""]}>
        {spec.fields.map((fm, i) => (
          <tr key={i}>
            <Td>
              {editable ? (
                <Select aria-label="Target field" value={fm.target} onChange={(e) => update(i, { ...fm, target: e.target.value })}>
                  {[...new Set([fm.target, ...targets])].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              ) : (
                <code className="text-sm">{fm.target}</code>
              )}
              {fm.target === spec.keyField && <Badge tone="accent">match key</Badge>}
            </Td>
            <Td>
              {editable && columns.length ? (
                <Select
                  aria-label="Source column"
                  value={sourceList(fm.source).find((s) => columns.includes(s)) ?? sourceList(fm.source)[0] ?? ""}
                  onChange={(e) => update(i, { ...fm, source: e.target.value || undefined })}
                >
                  <option value="">(none / default)</option>
                  {[...new Set([...sourceList(fm.source), ...columns])].map((c) => (
                    <option key={c} value={c}>
                      {c}
                      {columns.includes(c) ? "" : " (not in file)"}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="text-sm">{sourceList(fm.source).join(" | ") || <span className="muted">default</span>}</span>
              )}
            </Td>
            <Td>
              <div className="flex flex-wrap gap-1">
                {fm.transforms.length ? fm.transforms.map((t, j) => <Badge key={j}>{describeTransform(t)}</Badge>) : <span className="muted text-xs">none</span>}
              </div>
            </Td>
            <Td className="text-right whitespace-nowrap">
              {editable && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(i);
                      setJson(JSON.stringify(fm, null, 2));
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={fm.target === spec.keyField}
                    onClick={() => {
                      setSpec({ ...spec, fields: spec.fields.filter((_, j) => j !== i) });
                      setDirty(true);
                    }}
                  >
                    Remove
                  </Button>
                </>
              )}
            </Td>
          </tr>
        ))}
      </Table>
      {editable && (
        <Button
          size="sm"
          variant="secondary"
          className="mt-3"
          onClick={() => {
            setSpec({ ...spec, fields: [...spec.fields, { target: "custom.new_field", source: columns[0], transforms: [] }] });
            setDirty(true);
          }}
        >
          Add field
        </Button>
      )}
      {!columns.length && <p className="muted mt-2 text-xs">Click "Preview 20 rows" to load the source's columns.</p>}

      {preview && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold">Preview</h3>
          <Table head={["Key", "Fields", "Consent", "Issues"]}>
            {preview.results.map((r, i) => (
              <tr key={i}>
                <Td className="font-mono text-xs">{r.record?.key ?? <span className="muted">—</span>}</Td>
                <Td className="max-w-md">
                  <div className="font-mono text-xs break-all">{r.record ? Object.entries(r.record.fields).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ") : ""}</div>
                  {r.record?.lists && <div className="muted text-xs">lists: {r.record.lists.join(", ")}</div>}
                </Td>
                <Td className="text-xs">
                  {r.record?.consent
                    ? Object.entries(r.record.consent).map(([ch, c]) => (
                        <div key={ch}>
                          {ch}: <b>{c.status}</b>
                          {c.at ? ` (${c.at.slice(0, 10)})` : ""}
                        </div>
                      ))
                    : "—"}
                </Td>
                <Td className="text-xs">
                  {r.skipped && <div className="text-warn">skipped: {r.skipped}</div>}
                  {r.errors.map((e, j) => (
                    <div key={j} className="text-danger">
                      {e}
                    </div>
                  ))}
                </Td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit field mapping"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                try {
                  update(editing!, JSON.parse(json) as FieldMapping);
                  setEditing(null);
                } catch {
                  setMsg({ tone: "danger", text: "That isn't valid JSON." });
                }
              }}
            >
              Apply
            </Button>
          </>
        }
      >
        <p className="muted mb-2 text-sm">
          Transforms run in order. Available: {TRANSFORM_OPS.join(", ")}. Examples: <code>{'{"op":"phoneE164","defaultCountry":"AU"}'}</code>, <code>{'{"op":"valueMap","map":{"Y":"subscribed"}}'}</code>,{" "}
          <code>{'{"op":"date","input":"eu"}'}</code>.
        </p>
        <Textarea rows={14} value={json} onChange={(e) => setJson(e.target.value)} aria-label="Field mapping JSON" />
        <AddTransform
          onAdd={(t) => {
            try {
              const fm = JSON.parse(json) as FieldMapping;
              fm.transforms = [...(fm.transforms ?? []), t];
              setJson(JSON.stringify(fm, null, 2));
            } catch {
              /* ignore */
            }
          }}
        />
      </Modal>
    </Card>
  );
}

function AddTransform({ onAdd }: { onAdd: (t: Record<string, unknown> & { op: string }) => void }) {
  const [op, setOp] = useState("trim");
  const [arg, setArg] = useState("");
  const needsArg: Record<string, string> = { split: "separator", concat: "separator", default: "value", regexReplace: "pattern", phoneE164: "defaultCountry", date: "input", list: "separator" };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Select aria-label="Transform" value={op} onChange={(e) => setOp(e.target.value)} className="w-40">
        {TRANSFORM_OPS.filter((o) => o !== "valueMap").map((o) => (
          <option key={o}>{o}</option>
        ))}
      </Select>
      {needsArg[op] && <Input aria-label={needsArg[op]} placeholder={needsArg[op]} value={arg} onChange={(e) => setArg(e.target.value)} className="w-48" />}
      <Button size="sm" variant="secondary" onClick={() => onAdd(needsArg[op] ? { op, [needsArg[op]!]: arg } : { op })}>
        Add transform
      </Button>
    </div>
  );
}
